import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { esSoloLecturaClaro, esContratista, BannerSoloLectura } from "./permisos";

const C = { red: "#E30613", navy: "#1a2b4a", slate: "#64748b", light: "#f8fafc" };

// Rangos usando % de disponibilidad directamente (igual que el Home)
// N0: 100%, N1: >=99.2% <100%, N2: >=98.3% <99.2%, N3: >=96.7% <98.3%
// N4: >=94.7% <96.7%, N5: >=85% <94.7%, N6: <85%
const CATEGORIAS = [
  { nivel: "N0", minPct: 100,  maxPct: 100,  color: "#16a34a", colorLight: "#dcfce7" },
  { nivel: "N1", minPct: 99.2, maxPct: 100,  color: "#22c55e", colorLight: "#dcfce7" },
  { nivel: "N2", minPct: 98.3, maxPct: 99.2, color: "#84cc16", colorLight: "#ecfccb" },
  { nivel: "N3", minPct: 96.7, maxPct: 98.3, color: "#eab308", colorLight: "#fef9c3" },
  { nivel: "N4", minPct: 94.7, maxPct: 96.7, color: "#f97316", colorLight: "#ffedd5" },
  { nivel: "N5", minPct: 85,   maxPct: 94.7, color: "#ef4444", colorLight: "#fee2e2" },
  { nivel: "N6", minPct: 0,    maxPct: 85,   color: "#991b1b", colorLight: "#fee2e2" },
];

// Categoría según % de disponibilidad (mismo criterio que el Home y el backend)
const getCatPct = (pct) => {
  const p = parseFloat(pct ?? 100);
  if (p >= 100) return CATEGORIAS[0];
  if (p >= 99.2) return CATEGORIAS[1];
  if (p >= 98.3) return CATEGORIAS[2];
  if (p >= 96.7) return CATEGORIAS[3];
  if (p >= 94.7) return CATEGORIAS[4];
  if (p >= 85)   return CATEGORIAS[5];
  return CATEGORIAS[6];
};

// Horas de indisponibilidad (para mostrar en tabla, no para clasificar)
const calcH = (porc, dias) => Math.max((1 - parseFloat(porc ?? 100) / 100) * (dias || 30) * 24, 0);

// Semáforo: cuánto le falta para pasar a la siguiente categoría (en horas)
const getSem = (horasCalc, cat) => {
  if (cat.nivel === "N0") return { color: "#16a34a", pct: 0, texto: fmtH(5.95), siguiente: "N1" };
  if (cat.nivel === "N6") return { color: "#991b1b", pct: 100, texto: "Máx. categoría", siguiente: null };
  // Rangos en horas para referencia semáforo (límites superiores de cada cat)
  const limHoras = { N1: 5.95, N2: 12.583, N3: 24.55, N4: 39.433, N5: 111.6 };
  const idx = CATEGORIAS.findIndex(c => c.nivel === cat.nivel);
  const sigNivel = CATEGORIAS[idx + 1].nivel;
  const limSup = limHoras[cat.nivel] || 5.95;
  const limInf = idx > 0 ? (limHoras[CATEGORIAS[idx-1]?.nivel] || 0) : 0;
  const faltan = Math.max(limSup - horasCalc, 0);
  const rango = limSup - limInf;
  const usado = Math.max(horasCalc - limInf, 0);
  const pct = rango > 0 ? Math.min((usado / rango) * 100, 100) : 100;
  const color = pct >= 85 ? "#ef4444" : pct >= 50 ? "#f97316" : pct >= 25 ? "#eab308" : "#16a34a";
  return { color, pct: Math.round(pct), texto: fmtH(faltan), siguiente: sigNivel };
};
function fmtH(h) {
  if (h <= 0) return "0min";
  if (h < 1) return `${Math.round(h * 60)}min`;
  return `${Math.floor(h)}h ${Math.round((h % 1) * 60)}min`;
}
const fmtMes = (f) => {
  if (!f) return "—";
  const s = f.length === 7 ? f + "-01" : f;
  return new Date(s + "T12:00:00").toLocaleDateString("es-CO", { month: "short", year: "numeric" }).replace(".", "");
};
const enrich = (n) => {
  const h = calcH(n.porc_dispo_real, n.dias_mes);
  // Usar od.categoria directamente del backend (coherente con todas las vistas)
  const c = CATEGORIAS.find(cat => cat.nivel === n.categoria) || CATEGORIAS[0];
  return { ...n, horasCalc: h, catReal: c, semaforo: getSem(h, c) };
};

const thS = { background: "#1a2b4a", color: "white", padding: "8px 10px", textAlign: "left", fontSize: "11px", fontWeight: "600", whiteSpace: "nowrap" };
const tdS = { padding: "7px 10px", borderBottom: "1px solid #e2e8f0", fontSize: "11px", whiteSpace: "nowrap" };
const cardS = { background: "white", borderRadius: "12px", padding: "20px", marginBottom: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" };
const selS = { width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "12px", boxSizing: "border-box", cursor: "pointer", background: "white" };
const btnPag = (d) => ({ padding: "5px 12px", borderRadius: "6px", border: "none", cursor: d ? "not-allowed" : "pointer", fontSize: "11px", fontWeight: "600", background: d ? "#f1f5f9" : "#e2e8f0", color: d ? "#cbd5e1" : C.navy });

function KpiCard({ cat, valor, activo, onClick }) {
  return (
    <div onClick={onClick} style={{ background: activo ? cat.color : cat.colorLight, color: activo ? "white" : cat.color, padding: "10px 14px", borderRadius: "10px", cursor: "pointer", border: `2px solid ${cat.color}`, minWidth: "68px", textAlign: "center", transition: "all 0.15s", flexShrink: 0, userSelect: "none" }}>
      <div style={{ fontSize: "13px", fontWeight: "800" }}>{cat.nivel}</div>
      <div style={{ fontSize: "20px", fontWeight: "800" }}>{valor}</div>
      <div style={{ fontSize: "9px", marginTop: "2px" }}>nodos</div>
    </div>
  );
}


// ── Checkbox visual estilo Excel ─────────────────────────────────────────
function Checkbox({ checked, indeterminate }) {
  return (
    <div style={{ width: "14px", height: "14px", borderRadius: "3px", flexShrink: 0,
      border: `2px solid ${checked || indeterminate ? "#E30613" : "#94a3b8"}`,
      background: checked ? "#E30613" : "white",
      display: "flex", alignItems: "center", justifyContent: "center" }}>
      {checked && <span style={{ color: "white", fontSize: "9px", fontWeight: "800", lineHeight: 1 }}>✓</span>}
      {!checked && indeterminate && <span style={{ color: "#E30613", fontSize: "11px", fontWeight: "800", lineHeight: 1 }}>—</span>}
    </div>
  );
}

// ── Dropdown multiselect estilo Excel ────────────────────────────────────
function DropdownMulti({ id, label, opciones, valor, onChange, placeholder = "Todos", showBadge = true }) {
  const containerRef   = useRef(null);
  const panelRef       = useRef(null);
  const isOpen         = useRef(false);
  const opcionesCongeladas = useRef(opciones); // congela opciones mientras está abierto

  const todosMarcados  = valor === null;
  const ningunoMarcado = Array.isArray(valor) && valor.length === 0;
  const parcial        = Array.isArray(valor) && valor.length > 0 && valor.length < opciones.length;
  const hayFiltro      = !todosMarcados;
  const selCount       = todosMarcados ? opciones.length : (Array.isArray(valor) ? valor.length : 0);
  const esMarcado      = (op) => todosMarcados || (Array.isArray(valor) && valor.includes(op));

  const texto = todosMarcados ? placeholder
    : ningunoMarcado ? "(Ninguno)"
    : selCount <= 3 ? (Array.isArray(valor) ? valor.join(", ") : "")
    : `${selCount} de ${opciones.length}`;

  const buildPanel = (opts, val) => {
    const panel = panelRef.current;
    if (!panel) return;
    panel.innerHTML = "";
    const todosM = val === null;
    const parcialM = Array.isArray(val) && val.length > 0 && val.length < opts.length;

    const rowAll = document.createElement("div");
    rowAll.style.cssText = "display:flex;align-items:center;gap:8px;padding:7px 12px;cursor:pointer;border-bottom:1px solid #f1f5f9;user-select:none;font-size:12px;color:#1a2b4a;font-weight:600;";
    rowAll.onmouseenter = () => rowAll.style.background = "#f8fafc";
    rowAll.onmouseleave = () => rowAll.style.background = "white";
    rowAll.onclick = (e) => { e.stopPropagation(); onChange(todosM ? [] : null); };
    const chkAll = document.createElement("div");
    chkAll.style.cssText = `width:14px;height:14px;border-radius:3px;flex-shrink:0;border:2px solid ${todosM || parcialM ? "#E30613" : "#94a3b8"};background:${todosM ? "#E30613" : "white"};display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:800;color:white;`;
    if (todosM) chkAll.textContent = "✓";
    if (!todosM && parcialM) { chkAll.style.color = "#E30613"; chkAll.textContent = "—"; }
    rowAll.appendChild(chkAll);
    rowAll.appendChild(document.createTextNode("(Seleccionar todo)"));
    panel.appendChild(rowAll);

    opts.forEach((op) => {
      const row = document.createElement("div");
      row.style.cssText = "display:flex;align-items:center;gap:8px;padding:6px 12px;cursor:pointer;user-select:none;font-size:12px;color:#1a2b4a;";
      row.onmouseenter = () => row.style.background = "#f8fafc";
      row.onmouseleave = () => row.style.background = "white";
      row.onclick = (e) => {
        e.stopPropagation();
        if (todosM) {
          onChange(opts.filter(x => x !== op));
        } else if (Array.isArray(val)) {
          const next = val.includes(op) ? val.filter(x => x !== op) : [...val, op];
          onChange(next.length === opts.length ? null : next);
        }
      };
      const marcado = val === null || (Array.isArray(val) && val.includes(op));
      const chk = document.createElement("div");
      chk.style.cssText = `width:14px;height:14px;border-radius:3px;flex-shrink:0;border:2px solid ${marcado ? "#E30613" : "#94a3b8"};background:${marcado ? "#E30613" : "white"};display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:800;color:white;`;
      if (marcado) chk.textContent = "✓";
      row.appendChild(chk);
      row.appendChild(document.createTextNode(op));
      panel.appendChild(row);
    });
  };

  const openPanel = () => {
    opcionesCongeladas.current = opciones;
    buildPanel(opciones, valor); // construir con opciones y valor actuales al abrir
    isOpen.current = true;
    if (panelRef.current) panelRef.current.style.display = "block";
  };
  const closePanel = () => {
    isOpen.current = false;
    if (panelRef.current) panelRef.current.style.display = "none";
  };
  const togglePanel = () => isOpen.current ? closePanel() : openPanel();

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) closePanel();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!isOpen.current) return; // solo reconstruir si está abierto
    buildPanel(opcionesCongeladas.current, valor);
    if (panelRef.current) panelRef.current.style.display = "block";
  }, [valor]); // solo depende de valor, NO de opciones

  const selS = { width: "100%", padding: "8px 10px", borderRadius: "8px", fontSize: "12px", boxSizing: "border-box", cursor: "pointer", background: "white", border: hayFiltro ? "1.5px solid #E30613" : "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "4px" };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <label style={{ fontSize: "10px", color: "#64748b", fontWeight: "600", display: "block", marginBottom: "4px" }}>
        {label}
        {hayFiltro && showBadge && (
          <span style={{ marginLeft: "5px", background: "#E30613", color: "white", borderRadius: "10px", padding: "0 5px", fontSize: "9px" }}>
            {selCount}/{opciones.length}
          </span>
        )}
      </label>
      <div onClick={togglePanel} style={selS}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: hayFiltro ? "#1a2b4a" : "#94a3b8", fontSize: "12px", flex: 1 }}>{texto}</span>
        <span style={{ fontSize: "9px", color: "#64748b", flexShrink: 0 }}>▼</span>
      </div>
      <div ref={panelRef} style={{ display: "none", position: "absolute", top: "calc(100% + 3px)", left: 0, zIndex: 9999,
        background: "white", border: "1px solid #cbd5e1", borderRadius: "8px",
        boxShadow: "0 6px 20px rgba(0,0,0,0.15)", minWidth: "100%", maxHeight: "240px",
        overflowY: "auto", padding: "4px 0" }} />
    </div>
  );
}

// ── DropdownSingle: solo permite escoger UNA opción ──────────────────────────
function DropdownSingle({ id, label, opciones, valor, onChange, placeholder = "Seleccionar" }) {
  const containerRef = useRef(null);
  const panelRef     = useRef(null);
  const isOpen       = useRef(false);

  const hayFiltro = valor !== null && valor !== undefined;
  const texto     = hayFiltro ? valor : placeholder;

  const buildPanel = (opts, val) => {
    const panel = panelRef.current;
    if (!panel) return;
    panel.innerHTML = "";
    opts.forEach((op) => {
      const row = document.createElement("div");
      const activo = val === op;
      row.style.cssText = `display:flex;align-items:center;gap:8px;padding:7px 12px;cursor:pointer;user-select:none;font-size:12px;color:#1a2b4a;background:${activo ? "#fef2f2" : "white"};font-weight:${activo ? "700" : "400"};`;
      row.onmouseenter = () => { if (!activo) row.style.background = "#f8fafc"; };
      row.onmouseleave = () => { row.style.background = activo ? "#fef2f2" : "white"; };
      row.onclick = (e) => {
        e.stopPropagation();
        onChange(op === val ? null : op); // click en el mismo lo deselecciona
        closePanel();
      };
      if (activo) {
        const dot = document.createElement("span");
        dot.textContent = "●";
        dot.style.cssText = "color:#E30613;font-size:8px;flex-shrink:0;";
        row.appendChild(dot);
      }
      row.appendChild(document.createTextNode(op));
      panel.appendChild(row);
    });
  };

  const openPanel  = () => { buildPanel(opciones, valor); isOpen.current = true;  if (panelRef.current) panelRef.current.style.display = "block"; };
  const closePanel = () => {                               isOpen.current = false; if (panelRef.current) panelRef.current.style.display = "none"; };
  const togglePanel = () => isOpen.current ? closePanel() : openPanel();

  useEffect(() => {
    const handler = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) closePanel(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => { if (isOpen.current) buildPanel(opciones, valor); }, [valor, opciones]);

  const selSt = { width: "100%", padding: "8px 10px", borderRadius: "8px", fontSize: "12px", boxSizing: "border-box", cursor: "pointer", background: "white", border: hayFiltro ? "1.5px solid #E30613" : "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "4px" };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <label style={{ fontSize: "10px", color: "#64748b", fontWeight: "600", display: "block", marginBottom: "4px" }}>
        {label}
      </label>
      <div onClick={togglePanel} style={selSt}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: hayFiltro ? "#1a2b4a" : "#94a3b8", fontSize: "12px", flex: 1 }}>{texto}</span>
        <span style={{ fontSize: "9px", color: "#64748b", flexShrink: 0 }}>▼</span>
      </div>
      <div ref={panelRef} style={{ display: "none", position: "absolute", top: "calc(100% + 3px)", left: 0, zIndex: 9999,
        background: "white", border: "1px solid #cbd5e1", borderRadius: "8px",
        boxShadow: "0 6px 20px rgba(0,0,0,0.15)", minWidth: "100%", maxHeight: "240px",
        overflowY: "auto", padding: "4px 0" }} />
    </div>
  );
}

// ── DropdownMultiSearch: multiselección con búsqueda + Enter para seleccionar ──
function DropdownMultiSearch({ id, label, opciones, valor, onChange, placeholder = "Todos" }) {
  const containerRef  = useRef(null);
  const inputRef      = useRef(null);
  const [abierto, setAbierto]   = useState(false);
  const [busqueda, setBusqueda] = useState("");

  const todosMarcados = valor === null;
  const selCount      = todosMarcados ? opciones.length : (Array.isArray(valor) ? valor.length : 0);
  const hayFiltro     = !todosMarcados && selCount > 0;
  const opsFiltradas  = busqueda.trim()
    ? opciones.filter(o => o.toLowerCase().includes(busqueda.toLowerCase()))
    : opciones;

  const texto = !hayFiltro ? placeholder
    : selCount <= 2 ? (Array.isArray(valor) ? valor.join(", ") : "")
    : `${selCount} nodos`;

  const toggle = (op) => {
    const cur = Array.isArray(valor) ? valor : [];
    const next = cur.includes(op) ? cur.filter(x => x !== op) : [...cur, op];
    onChange(next.length === 0 ? null : next);
  };

  const abrir  = () => { setAbierto(true);  setTimeout(() => inputRef.current?.focus(), 30); };
  const cerrar = () => { setAbierto(false); setBusqueda(""); };

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const handler = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) cerrar(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Enter: si hay exactamente 1 resultado filtrado, seleccionarlo y cerrar
  // Si hay varios, seleccionar todos los filtrados
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (opsFiltradas.length === 0) return;
      if (opsFiltradas.length === 1) {
        toggle(opsFiltradas[0]);
        cerrar();
      } else {
        // Seleccionar todos los que coinciden con la búsqueda
        const cur  = Array.isArray(valor) ? valor : [];
        const news = opsFiltradas.filter(o => !cur.includes(o));
        if (news.length > 0) onChange([...cur, ...news]);
        cerrar();
      }
    }
    if (e.key === "Escape") cerrar();
  };

  const selSt = {
    width: "100%", padding: "8px 10px", borderRadius: "8px", fontSize: "12px",
    boxSizing: "border-box", cursor: "pointer", background: "white",
    border: hayFiltro ? "1.5px solid #E30613" : "1px solid #e2e8f0",
    display: "flex", justifyContent: "space-between", alignItems: "center", gap: "4px"
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <label style={{ fontSize: "10px", color: "#64748b", fontWeight: "600", display: "block", marginBottom: "4px" }}>
        {label}
        {hayFiltro && <span style={{ marginLeft: "5px", background: "#E30613", color: "white", borderRadius: "10px", padding: "0 5px", fontSize: "9px" }}>{selCount}</span>}
      </label>

      {/* Trigger */}
      <div onClick={() => abierto ? cerrar() : abrir()} style={selSt}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: hayFiltro ? "#1a2b4a" : "#94a3b8", fontSize: "12px", flex: 1 }}>{texto}</span>
        <span style={{ fontSize: "9px", color: "#64748b", flexShrink: 0 }}>{abierto ? "▲" : "▼"}</span>
      </div>

      {/* Panel — solo se monta cuando está abierto */}
      {abierto && (
        <div style={{ position: "absolute", top: "calc(100% + 3px)", left: 0, zIndex: 9999,
          background: "white", border: "1px solid #cbd5e1", borderRadius: "8px",
          boxShadow: "0 6px 20px rgba(0,0,0,0.15)", minWidth: "200px", maxHeight: "300px",
          display: "flex", flexDirection: "column" }}>

          {/* Input búsqueda sticky */}
          <div style={{ padding: "7px 8px", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
            <input ref={inputRef} type="text" value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              onKeyDown={handleKeyDown}
              onClick={e => e.stopPropagation()}
              placeholder="Escribe sigla y Enter..."
              style={{ width: "100%", padding: "5px 8px", fontSize: "12px", border: "1px solid #e2e8f0",
                       borderRadius: "6px", outline: "none", boxSizing: "border-box" }} />
            <div style={{ fontSize: "10px", color: "#94a3b8", marginTop: "3px", paddingLeft: "2px" }}>
              Enter para seleccionar {opsFiltradas.length === 1 ? opsFiltradas[0] : `${opsFiltradas.length} resultados`}
            </div>
          </div>

          {/* Limpiar */}
          {hayFiltro && (
            <div onMouseDown={e => { e.preventDefault(); onChange(null); setBusqueda(""); }}
              style={{ padding: "6px 12px", fontSize: "11px", color: "#E30613", cursor: "pointer",
                       borderBottom: "1px solid #f1f5f9", fontWeight: "600", flexShrink: 0,
                       background: "white" }}
              onMouseEnter={e => e.currentTarget.style.background="#fef2f2"}
              onMouseLeave={e => e.currentTarget.style.background="white"}>
              ✕ Limpiar selección ({selCount})
            </div>
          )}

          {/* Lista */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {opsFiltradas.length === 0 ? (
              <div style={{ padding: "12px", fontSize: "12px", color: "#94a3b8", textAlign: "center" }}>Sin resultados</div>
            ) : opsFiltradas.map((op) => {
              const marcado = Array.isArray(valor) && valor.includes(op);
              return (
                <div key={op}
                  onMouseDown={e => { e.preventDefault(); toggle(op); }}
                  style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 12px",
                           cursor: "pointer", fontSize: "12px", color: "#1a2b4a",
                           background: marcado ? "#fef2f2" : "white" }}
                  onMouseEnter={e => { if (!marcado) e.currentTarget.style.background="#f8fafc"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = marcado ? "#fef2f2" : "white"; }}>
                  <div style={{ width: "14px", height: "14px", borderRadius: "3px", flexShrink: 0,
                    border: `2px solid ${marcado ? "#E30613" : "#94a3b8"}`,
                    background: marcado ? "#E30613" : "white", display: "flex", alignItems: "center",
                    justifyContent: "center", fontSize: "9px", fontWeight: "800", color: "white" }}>
                    {marcado ? "✓" : ""}
                  </div>
                  {op}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SemaforoNodos({ usuario }) {
  const soloLectura = esSoloLecturaClaro(usuario) || esContratista(usuario);
  const API = process.env.REACT_APP_API_URL;
  const [vista, setVista] = useState("actual");
  const [filtroMes, setFiltroMes]         = useState(null);
  const [filtroJefe, setFiltroJefe]       = useState(null);
  const [filtroAliado, setFiltroAliado]   = useState(null);
  const [filtroCiudad, setFiltroCiudad]   = useState(null);
  const [filtroNodo, setFiltroNodo]       = useState(null);
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [filtroRiesgo, setFiltroRiesgo]   = useState(null);
  const [filtroPrioridad, setFiltroPrioridad] = useState(null); // multiselect (null = todos)
  const [sortCol, setSortCol] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [mesesDisp, setMesesDisp] = useState([]);
  const [dropOpen, setDropOpen] = useState("");

  const [datos, setDatos] = useState([]);
  const [todosNodos, setTodosNodos] = useState([]); // lista completa para filtro
  const [loading, setLoading] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [total, setTotal] = useState(0);
  const [totPag, setTotPag] = useState(1);
  const [kpis, setKpis] = useState({});

  // KPIs filtrados: se recalculan en el backend cuando hay filtros activos de jefe/aliado/ciudad
  const [kpisFiltrados, setKpisFiltrados] = useState(null);
  const [loadingKpis, setLoadingKpis] = useState(false);

  const [datosH, setDatosH] = useState([]);
  const [loadingH, setLoadingH] = useState(false);
  const [recurrenciaJefe, setRecurrenciaJefe] = useState([]);
  const [loadingRec, setLoadingRec] = useState(false);
  const [recFiltroJefe, setRecFiltroJefe] = useState(null);   // null = todos
  const [recFiltroRec, setRecFiltroRec]   = useState(null);   // null = todos

  // ── CUADRO RESUMEN CATEGORIA x PRIORIDAD (tabla pivote) ──────────────
  // pivot: null = todos; objeto con cat y/o prioridad = filtro activo
  const [resumenPivot, setResumenPivot]       = useState(null); // datos del backend
  const [loadingResumen, setLoadingResumen]   = useState(false);
  const [pivotFiltroCat, setPivotFiltroCat]   = useState(null); // "N0".."N6" | null
  const [pivotFiltroPrio, setPivotFiltroPrio] = useState(null); // "Alta","Media","Baja","Sin dato" | null

  const [paginaH, setPaginaH] = useState(1);
  const [totalH, setTotalH] = useState(0);
  const [totPagH, setTotPagH] = useState(1);
  const [kpisH, setKpisH] = useState({});

  useEffect(() => {
    fetch(`${API}/semaforo-nodos-meses`).then(r => r.json()).then(d => {
      if (Array.isArray(d) && d.length > 0) {
        const m = d.map(x => x.mes_key);
        setMesesDisp(m);
        setFiltroMes([m[0]]); // por defecto el más reciente (último corte)
      }
    }).catch(() => {});
  }, [API]);

  // Cargar lista de nodos del mes activo para el filtro
  useEffect(() => {
    if (!mesesDisp.length) return;
    const mesActivo = Array.isArray(filtroMes) && filtroMes.length ? filtroMes[0] : mesesDisp[0];
    const rol    = encodeURIComponent(usuario?.rol    || "");
    const cedula = encodeURIComponent(usuario?.cedula || "");
    const aliado = encodeURIComponent(usuario?.aliado || "");
    const mes    = encodeURIComponent(mesActivo || "");
    fetch(`${API}/semaforo-nodos-lista?rol=${rol}&cedula=${cedula}&aliado=${aliado}&mes=${mes}`)
      .then(r => r.json())
      .then(d => { if (Array.isArray(d)) setTodosNodos(d); })
      .catch(() => {});
  }, [API, usuario, filtroMes, mesesDisp]);

  const cargar = useCallback((pag = 1) => {
    if (!mesesDisp.length) return;
    setLoading(true);
    const mesActual = filtroMes === null ? mesesDisp[0] : (Array.isArray(filtroMes) && filtroMes.length ? filtroMes[0] : mesesDisp[0]);
    const p = new URLSearchParams({ rol: usuario?.rol || "", cedula: usuario?.cedula || "", mes: mesActual, page: pag });
    if (filtroCategoria) p.set("categoria", filtroCategoria);
    if (Array.isArray(filtroJefe)   && filtroJefe.length)   p.set("jefe",   filtroJefe.join(","));
    if (Array.isArray(filtroAliado) && filtroAliado.length) p.set("aliado", filtroAliado.join(","));
    if (Array.isArray(filtroCiudad) && filtroCiudad.length) p.set("ciudad", filtroCiudad.join(","));
    if (Array.isArray(filtroNodo)   && filtroNodo.length)   p.set("nodo",   filtroNodo.join(","));
    // Filtros de recurrencia → se envían al backend para paginación correcta
    if (recFiltroJefe) p.set("jefe",        recFiltroJefe);
    if (recFiltroRec)  p.set("recurrencia", recFiltroRec);
    // Filtro pivote categoría x prioridad: ambos van al backend
    if (pivotFiltroCat)  p.set("categoria", pivotFiltroCat);
    if (pivotFiltroPrio) p.set("prioridad", pivotFiltroPrio);
    // Filtro de prioridad del dropdown (solo si no hay filtro pivote activo de prioridad)
    else if (Array.isArray(filtroPrioridad) && filtroPrioridad.length) p.set("prioridad", filtroPrioridad.join(","));
    fetch(`${API}/semaforo-nodos?${p}`).then(r => r.json()).then(d => {
      if (d && Array.isArray(d.datos)) {
        setDatos(d.datos); setTotal(d.total || 0); setTotPag(d.totalPaginas || 1); setPagina(d.pagina || 1); setKpis(d.kpis || {});
      } else { setDatos([]); setTotal(0); setTotPag(1); setKpis({}); }
      setLoading(false);
    }).catch(() => { setDatos([]); setLoading(false); });
  }, [API, usuario, filtroMes, filtroCategoria, filtroJefe, filtroAliado, filtroCiudad, filtroNodo, filtroPrioridad, mesesDisp, recFiltroJefe, recFiltroRec, pivotFiltroCat, pivotFiltroPrio]);

  useEffect(() => {
    if (vista !== "actual" || !mesesDisp.length) return;
    // Debounce: esperar 300ms después del último cambio de filtro antes de llamar al backend
    // Así el dropdown permanece abierto mientras el usuario sigue seleccionando
    const timer = setTimeout(() => cargar(1), 300);
    return () => clearTimeout(timer);
  }, [cargar, vista]);

  // Cuando cambian filtros → pedir KPIs al backend con todos los filtros activos
  useEffect(() => {
    if (!mesesDisp.length || vista !== "actual") return;
    const jefeActivo   = Array.isArray(filtroJefe)   && filtroJefe.length   > 0;
    const nodoActivo   = Array.isArray(filtroNodo)   && filtroNodo.length   > 0;
    const aliadoActivo = Array.isArray(filtroAliado) && filtroAliado.length > 0;
    const ciudadActivo = Array.isArray(filtroCiudad) && filtroCiudad.length > 0;
    const prioActivo   = Array.isArray(filtroPrioridad) && filtroPrioridad.length > 0;

    if (!jefeActivo && !aliadoActivo && !ciudadActivo && !nodoActivo && !prioActivo && !pivotFiltroCat && !pivotFiltroPrio) { setKpisFiltrados(null); return; }

    const timer = setTimeout(() => {
      setLoadingKpis(true);
      const mesActual = filtroMes === null ? mesesDisp[0] : (Array.isArray(filtroMes) && filtroMes.length ? filtroMes[0] : mesesDisp[0]);
      const p = new URLSearchParams({ rol: usuario?.rol || "", cedula: usuario?.cedula || "", mes: mesActual, page: 1 });
      if (filtroCategoria) p.set("categoria", filtroCategoria);
      if (jefeActivo)   p.set("jefe",   filtroJefe.join(","));
      if (aliadoActivo) p.set("aliado", filtroAliado.join(","));
      if (ciudadActivo) p.set("ciudad", filtroCiudad.join(","));
      if (nodoActivo)   p.set("nodo",   filtroNodo.join(","));
      if (pivotFiltroCat)  p.set("categoria", pivotFiltroCat);
      if (pivotFiltroPrio) p.set("prioridad", pivotFiltroPrio);
      else if (prioActivo) p.set("prioridad", filtroPrioridad.join(","));
      fetch(`${API}/semaforo-nodos?${p}`).then(r => r.json()).then(d => {
        setKpisFiltrados(d.kpis || null);
        setLoadingKpis(false);
      }).catch(() => { setKpisFiltrados(null); setLoadingKpis(false); });
    }, 300);
    return () => clearTimeout(timer);
  }, [API, usuario, filtroMes, filtroJefe, filtroAliado, filtroCiudad, filtroNodo, filtroPrioridad, filtroCategoria, mesesDisp, vista, pivotFiltroCat, pivotFiltroPrio]);

  const cargarH = useCallback((pag = 1) => {
    if (!mesesDisp.length) return;
    setLoadingH(true);
    const mesActual = filtroMes === null ? mesesDisp[0] : (Array.isArray(filtroMes) && filtroMes.length ? filtroMes[0] : mesesDisp[0]);
    const p = new URLSearchParams({ rol: usuario?.rol || "", cedula: usuario?.cedula || "", mes: mesActual, page: pag });
    if (filtroCategoria) p.set("categoria", filtroCategoria);
    if (Array.isArray(filtroJefe)   && filtroJefe.length)   p.set("jefe",   filtroJefe.join(","));
    if (Array.isArray(filtroAliado) && filtroAliado.length) p.set("aliado", filtroAliado.join(","));
    if (Array.isArray(filtroCiudad) && filtroCiudad.length) p.set("ciudad", filtroCiudad.join(","));
    if (Array.isArray(filtroNodo)   && filtroNodo.length)   p.set("nodo",   filtroNodo.join(","));
    // Filtros de recurrencia → se envían al backend para paginación correcta
    if (recFiltroJefe) p.set("jefe",        recFiltroJefe);
    if (recFiltroRec)  p.set("recurrencia", recFiltroRec);
    // Filtro pivote categoría x prioridad: ambos van al backend
    if (pivotFiltroCat)  p.set("categoria", pivotFiltroCat);
    if (pivotFiltroPrio) p.set("prioridad", pivotFiltroPrio);
    // Filtro de prioridad del dropdown (solo si no hay filtro pivote activo de prioridad)
    else if (Array.isArray(filtroPrioridad) && filtroPrioridad.length) p.set("prioridad", filtroPrioridad.join(","));
    fetch(`${API}/semaforo-nodos-historico?${p}`).then(r => r.json()).then(d => {
      if (d && Array.isArray(d.datos)) {
        setDatosH(d.datos.map(enrich)); setTotalH(d.totalNodos || 0); setTotPagH(d.totalPaginas || 1); setPaginaH(d.pagina || 1); setKpisH(d.kpis || {});
      } else { setDatosH([]); setTotalH(0); setTotPagH(1); setKpisH({}); }
      setLoadingH(false);
    }).catch(() => { setDatosH([]); setLoadingH(false); });
  }, [API, usuario, filtroMes, filtroCategoria, filtroJefe, filtroAliado, filtroCiudad, filtroNodo, filtroPrioridad, mesesDisp, recFiltroJefe, recFiltroRec, pivotFiltroCat, pivotFiltroPrio]);

  useEffect(() => {
    if (vista !== "historico" || !mesesDisp.length) return;
    const timer = setTimeout(() => cargarH(1), 300);
    return () => clearTimeout(timer);
  }, [cargarH, vista]);

  // Cargar resumen de recurrencia por jefe cuando cambia el mes o filtros en vista histórica
  const cargarRecurrencia = useCallback(() => {
    if (!mesesDisp.length) return;
    setLoadingRec(true);
    const mesActual = filtroMes === null ? mesesDisp[0] : (Array.isArray(filtroMes) && filtroMes.length ? filtroMes[0] : mesesDisp[0]);
    const p = new URLSearchParams({ rol: usuario?.rol || "", cedula: usuario?.cedula || "", mes: mesActual });
    if (Array.isArray(filtroJefe)      && filtroJefe.length)      p.set("jefe",      filtroJefe.join(","));
    if (Array.isArray(filtroAliado)    && filtroAliado.length)    p.set("aliado",    filtroAliado.join(","));
    if (Array.isArray(filtroCiudad)    && filtroCiudad.length)    p.set("ciudad",    filtroCiudad.join(","));
    if (Array.isArray(filtroNodo)      && filtroNodo.length)      p.set("nodo",      filtroNodo.join(","));
    // Prioridad: filtro del dropdown o del pivote (lo que esté activo)
    if (pivotFiltroPrio) p.set("prioridad", pivotFiltroPrio);
    else if (Array.isArray(filtroPrioridad) && filtroPrioridad.length) p.set("prioridad", filtroPrioridad.join(","));
    // Categoría: filtro general o del pivote
    if (pivotFiltroCat) p.set("categoria", pivotFiltroCat);
    else if (filtroCategoria) p.set("categoria", filtroCategoria);
    fetch(`${API}/semaforo-recurrencia-jefe?${p}`).then(r => r.json()).then(d => {
      setRecurrenciaJefe(Array.isArray(d) ? d : []);
      setLoadingRec(false);
    }).catch(() => { setRecurrenciaJefe([]); setLoadingRec(false); });
  }, [API, usuario, filtroMes, filtroJefe, filtroAliado, filtroCiudad, filtroNodo, filtroPrioridad, filtroCategoria, mesesDisp, pivotFiltroCat, pivotFiltroPrio]);

  useEffect(() => {
    if (vista !== "historico" || !mesesDisp.length) return;
    const timer = setTimeout(() => cargarRecurrencia(), 300);
    return () => clearTimeout(timer);
  }, [cargarRecurrencia, vista]);

  // ── Cargar resumen CATEGORIA x PRIORIDAD (cuadro pivote) ─────────────
  // Responde a TODOS los filtros (jefe, aliado, ciudad, nodo, categoría, prioridad del dropdown).
  // EXCEPCIÓN: el filtro pivote (pivotFiltroPrio = click en columna) NO se envía a sí mismo,
  // para que al hacer click en una columna no se colapse a una sola celda y se pueda seguir
  // navegando por las demás columnas.
  const cargarResumen = useCallback(() => {
    if (!mesesDisp.length) return;
    setLoadingResumen(true);
    const mesActual = filtroMes === null ? mesesDisp[0] : (Array.isArray(filtroMes) && filtroMes.length ? filtroMes[0] : mesesDisp[0]);
    const p = new URLSearchParams({ rol: usuario?.rol || "", cedula: usuario?.cedula || "", mes: mesActual });
    if (Array.isArray(filtroJefe)   && filtroJefe.length)   p.set("jefe",   filtroJefe.join(","));
    if (Array.isArray(filtroAliado) && filtroAliado.length) p.set("aliado", filtroAliado.join(","));
    if (Array.isArray(filtroCiudad) && filtroCiudad.length) p.set("ciudad", filtroCiudad.join(","));
    if (Array.isArray(filtroNodo)   && filtroNodo.length)   p.set("nodo",   filtroNodo.join(","));
    if (recFiltroJefe) p.set("jefe",        recFiltroJefe);
    if (recFiltroRec)  p.set("recurrencia", recFiltroRec);
    // Categoría del filtro general (no enviamos la del pivote para no colapsar filas)
    if (filtroCategoria) p.set("categoria", filtroCategoria);
    // Prioridad del dropdown SÍ se envía (esta es la corrección):
    // así al marcar "Alta" o "Media" en el dropdown, el cuadro respeta el filtro.
    // Solo el filtro del click-en-columna del propio pivote (pivotFiltroPrio) queda fuera.
    if (Array.isArray(filtroPrioridad) && filtroPrioridad.length) p.set("prioridad", filtroPrioridad.join(","));
    fetch(`${API}/semaforo-resumen-categoria-prioridad?${p}`).then(r => r.json()).then(d => {
      setResumenPivot(d && d.matriz ? d : null);
      setLoadingResumen(false);
    }).catch(() => { setResumenPivot(null); setLoadingResumen(false); });
  }, [API, usuario, filtroMes, filtroJefe, filtroAliado, filtroCiudad, filtroNodo, filtroPrioridad, filtroCategoria, mesesDisp, recFiltroJefe, recFiltroRec]);

  useEffect(() => {
    if (!mesesDisp.length) return;
    const timer = setTimeout(() => cargarResumen(), 300);
    return () => clearTimeout(timer);
  }, [cargarResumen]);

  const datosEnriq = useMemo(() => datos.map(enrich), [datos]);

  // Clasifica el nivel de riesgo de un nodo enriquecido
  const getRiesgoLabel = (n) => {
    if (n.catReal.nivel === "N0") return "OK";
    if (n.catReal.nivel === "N6") return "Crítico";
    if (n.semaforo.pct >= 85) return "Crítico";
    if (n.semaforo.pct >= 50) return "Alto";
    if (n.semaforo.pct >= 25) return "Medio";
    return "Bajo";
  };

  const datosFilt = useMemo(() => {
    // Todos los filtros van al backend excepto Riesgo (calculado localmente desde porc_dispo_real)
    const okRiesgo = (n) => filtroRiesgo === null || (Array.isArray(filtroRiesgo) && (filtroRiesgo.length === 0 || filtroRiesgo.includes(getRiesgoLabel(n))));
    // Los filtros de recurrencia y pivote (cat+prioridad) van al backend, solo filtrar riesgo localmente
    let base = datosEnriq.filter(n => okRiesgo(n));
    if (!sortCol) return base;
    return [...base].sort((a, b) => {
      let vA, vB;
      if (sortCol === "id_nodo")         { vA = a.id_nodo;                         vB = b.id_nodo; }
      else if (sortCol === "nombre_nodo"){ vA = a.nombre_nodo || "";                vB = b.nombre_nodo || ""; }
      else if (sortCol === "ciudad")     { vA = a.ciudad || "";                     vB = b.ciudad || ""; }
      else if (sortCol === "nombre_jefe"){ vA = a.nombre_jefe || "";                vB = b.nombre_jefe || ""; }
      else if (sortCol === "aliado")     { vA = a.aliado || "";                     vB = b.aliado || ""; }
      else if (sortCol === "categoria")  { vA = a.catReal.nivel;                    vB = b.catReal.nivel; }
      else if (sortCol === "porc_dispo") { vA = parseFloat(a.porc_dispo_real||100); vB = parseFloat(b.porc_dispo_real||100); }
      else if (sortCol === "h_indisp")   { vA = a.horasCalc;                        vB = b.horasCalc; }
      else if (sortCol === "riesgo")     { vA = a.semaforo.pct;                     vB = b.semaforo.pct; }
      else return 0;
      if (typeof vA === "number") return sortDir === "asc" ? vA - vB : vB - vA;
      return sortDir === "asc" ? vA.localeCompare(vB) : vB.localeCompare(vA);
    });
  }, [datosEnriq, filtroRiesgo, recFiltroJefe, recFiltroRec, sortCol, sortDir]);

  // KPIs: usar los del backend siempre (son precisos sobre todos los datos, no solo la página)
  // Si hay filtros activos de jefe/aliado/ciudad → usar kpisFiltrados que vienen del backend con esos filtros
  // Para filtros locales (nodo, riesgo) → calcular sobre datosFilt que sí tenemos completo por página
  const kpisActual = useMemo(() => {
    const base = kpisFiltrados || kpis;
    // Solo si hay filtro de riesgo (local) recalcular desde datosFilt
    const hayFiltroRiesgo = Array.isArray(filtroRiesgo) && filtroRiesgo.length > 0;
    if (hayFiltroRiesgo) {
      const counts = {};
      CATEGORIAS.forEach(c => { counts[c.nivel] = 0; });
      datosFilt.forEach(n => { if (counts[n.catReal.nivel] !== undefined) counts[n.catReal.nivel]++; });
      counts.total = datosFilt.length;
      counts.ofensores = ["N2","N3","N4","N5","N6"].reduce((s, n) => s + (counts[n]||0), 0);
      return counts;
    }
    // Sin filtros locales: usar KPIs del backend directamente
    const ofensores = ["N2","N3","N4","N5","N6"].reduce((s, n) => s + (parseInt(base[n])||0), 0);
    return { ...base, ofensores };
  }, [kpis, kpisFiltrados, datosFilt, filtroRiesgo]);

  // optsNodos usa la lista completa (no depende del mes/filtro activo)
  const optsNodos  = useMemo(() => todosNodos.length ? todosNodos : [...new Set(datosEnriq.map(n => n.id_nodo).filter(Boolean))].sort(), [todosNodos, datosEnriq]);

  // Opciones bidireccionales: cada filtro muestra sus opciones considerando todos los OTROS filtros activos
  const optsJefe = useMemo(() => {
    const base = datosEnriq.filter(n => {
      const okA = filtroAliado === null || (Array.isArray(filtroAliado) && (filtroAliado.length === 0 || filtroAliado.includes(n.aliado)));
      const okC = filtroCiudad === null || (Array.isArray(filtroCiudad) && (filtroCiudad.length === 0 || filtroCiudad.includes(n.ciudad)));
      return okA && okC;
    });
    return [...new Set(base.map(n => n.nombre_jefe).filter(Boolean))].sort();
  }, [datosEnriq, filtroAliado, filtroCiudad]);

  const optsAliado = useMemo(() => {
    const base = datosEnriq.filter(n => {
      const okJ = filtroJefe === null || (Array.isArray(filtroJefe) && (filtroJefe.length === 0 || filtroJefe.includes(n.nombre_jefe)));
      const okC = filtroCiudad === null || (Array.isArray(filtroCiudad) && (filtroCiudad.length === 0 || filtroCiudad.includes(n.ciudad)));
      return okJ && okC;
    });
    return [...new Set(base.map(n => n.aliado).filter(Boolean))].sort();
  }, [datosEnriq, filtroJefe, filtroCiudad]);

  const optsCiudad = useMemo(() => {
    const base = datosEnriq.filter(n => {
      const okJ = filtroJefe === null || (Array.isArray(filtroJefe) && (filtroJefe.length === 0 || filtroJefe.includes(n.nombre_jefe)));
      const okA = filtroAliado === null || (Array.isArray(filtroAliado) && (filtroAliado.length === 0 || filtroAliado.includes(n.aliado)));
      return okJ && okA;
    });
    return [...new Set(base.map(n => n.ciudad).filter(Boolean))].sort();
  }, [datosEnriq, filtroJefe, filtroAliado]);

  // Opciones de prioridad: acumulamos TODAS las prioridades que hemos visto
  // en un ref, para que el dropdown no se quede vacío cuando el usuario
  // ya seleccionó una (si filtras por "Alta", el backend solo devuelve "Alta",
  // pero el dropdown debe seguir mostrando "Media", "Baja", etc. para poder cambiar).
  const prioridadesVistas = useRef(new Set());
  useEffect(() => {
    if (resumenPivot && Array.isArray(resumenPivot.prioridades)) {
      resumenPivot.prioridades.forEach(p => prioridadesVistas.current.add(p));
    }
  }, [resumenPivot]);
  const optsPrioridad = useMemo(() => {
    // Unión: lo que está en el resumen actual + lo que hemos visto antes
    const set = new Set(prioridadesVistas.current);
    if (resumenPivot && Array.isArray(resumenPivot.prioridades)) {
      resumenPivot.prioridades.forEach(p => set.add(p));
    }
    // También incluir lo que el usuario ya tiene seleccionado (por si el ref aún no se pobló)
    if (Array.isArray(filtroPrioridad)) filtroPrioridad.forEach(p => set.add(p));
    if (set.size === 0) return ["Alta", "Media", "Baja", "Sin dato"];
    // Orden estándar: Alta → Media → Baja → otros → Sin dato al final
    const orden = ["Alta", "Media", "Baja"];
    const arr = [...set];
    return [
      ...orden.filter(p => arr.includes(p)),
      ...arr.filter(p => !orden.includes(p) && p !== "Sin dato").sort(),
      ...(arr.includes("Sin dato") ? ["Sin dato"] : []),
    ];
  }, [resumenPivot, filtroPrioridad]);



  const { nodosHist, mesesHist } = useMemo(() => {
    const ms = [...new Set(datosH.map(n => n.mes_key))].sort((a, b) => b.localeCompare(a));
    const mp = {};
    datosH.forEach(n => {
      if (!mp[n.id_nodo]) mp[n.id_nodo] = { id_nodo: n.id_nodo, nombre_nodo: n.nombre_nodo, nombre_jefe: n.nombre_jefe, ciudad: n.ciudad, meses: {} };
      mp[n.id_nodo].meses[n.mes_key] = n;
    });
    let nodos = Object.values(mp).sort((a, b) => a.id_nodo.localeCompare(b.id_nodo));
    // Los filtros de recurrencia y jefe ya vienen filtrados desde el backend
    if (filtroNodo !== null && Array.isArray(filtroNodo) && filtroNodo.length) nodos = nodos.filter(n => filtroNodo.includes(n.id_nodo));
    return { nodosHist: nodos, mesesHist: ms };
  }, [datosH, filtroNodo, filtroCategoria, recFiltroJefe, recFiltroRec]);

  const limpiar = () => { setFiltroJefe(null); setFiltroAliado(null); setFiltroCiudad(null); setFiltroNodo(null); setFiltroCategoria(""); setFiltroRiesgo(null); setFiltroPrioridad(null); setSortCol(""); setSortDir("asc"); setDropOpen(""); setFiltroMes(mesesDisp.length ? [mesesDisp[0]] : null); setPagina(1); setPaginaH(1); setRecFiltroJefe(null); setRecFiltroRec(null); setPivotFiltroCat(null); setPivotFiltroPrio(null); };

  const cambiarVista = (v) => { setVista(v); setFiltroCategoria(""); setFiltroNodo(null); setPagina(1); setPaginaH(1); setPivotFiltroCat(null); setPivotFiltroPrio(null); };
  const toggleCat = (nivel) => { setFiltroCategoria(prev => prev === nivel ? "" : nivel); setPagina(1); setPaginaH(1); };

  // ── Handlers del cuadro resumen (pivote categoria x prioridad) ────
  // Click en celda: filtra por categoría Y prioridad
  const handlePivotCelda = (cat, prio) => {
    if (pivotFiltroCat === cat && pivotFiltroPrio === prio) {
      setPivotFiltroCat(null); setPivotFiltroPrio(null);
    } else {
      setPivotFiltroCat(cat); setPivotFiltroPrio(prio);
    }
    setPagina(1); setPaginaH(1);
  };
  // Click en encabezado de columna (prioridad): filtra solo por prioridad
  const handlePivotCol = (prio) => {
    if (pivotFiltroPrio === prio && pivotFiltroCat === null) {
      setPivotFiltroPrio(null);
    } else {
      setPivotFiltroPrio(prio); setPivotFiltroCat(null);
    }
    setPagina(1); setPaginaH(1);
  };
  // Click en total de fila (categoría): filtra solo por categoría
  const handlePivotFila = (cat) => {
    if (pivotFiltroCat === cat && pivotFiltroPrio === null) {
      setPivotFiltroCat(null);
    } else {
      setPivotFiltroCat(cat); setPivotFiltroPrio(null);
    }
    setPagina(1); setPaginaH(1);
  };
  const handlePivotTotal = () => {
    setPivotFiltroCat(null); setPivotFiltroPrio(null);
    setPagina(1); setPaginaH(1);
  };

  // ── Funciones helper para renderizar los cuadros resumen ─────
  // (se separan para reusarlas en layouts diferentes según vista)
  const renderCuadroPrioridad = () => (
    <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px", flexWrap: "wrap", gap: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "3px", height: "14px", background: C.red, borderRadius: "2px" }} />
                <span style={{ fontSize: "12px", fontWeight: "700", color: C.navy, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Resumen Categoría × Prioridad
                </span>
                <span style={{ fontSize: "11px", color: C.slate }}>
                  — {filtroMes === null ? fmtMes(mesesDisp[0]) : (Array.isArray(filtroMes) && filtroMes.length ? fmtMes(filtroMes[0]) : "")}
                </span>
              </div>
              {(pivotFiltroCat || pivotFiltroPrio) && (
                <button onClick={handlePivotTotal}
                  style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "20px", padding: "3px 10px", fontSize: "11px", color: C.red, fontWeight: "600", cursor: "pointer" }}>
                  ✕ Limpiar filtro
                </button>
              )}
            </div>

            {loadingResumen
              ? <div style={{ textAlign: "center", padding: "20px", color: C.slate, fontSize: "12px" }}>Cargando resumen...</div>
              : (() => {
                  // Paleta de colores para los distintos tipos de prioridad
                  const colorPrio = (prio) => {
                    const p = (prio || "").toString().toLowerCase();
                    if (p === "alta")       return { bg: "#fee2e2", color: "#991b1b", actBg: "#991b1b" };
                    if (p === "media")      return { bg: "#fef9c3", color: "#92400e", actBg: "#ca8a04" };
                    if (p === "baja")       return { bg: "#dcfce7", color: "#166534", actBg: "#16a34a" };
                    if (p === "sin dato")   return { bg: "#f1f5f9", color: "#475569", actBg: "#475569" };
                    return { bg: "#dbeafe", color: "#1d4ed8", actBg: "#1d4ed8" };
                  };

                  const cats        = resumenPivot.categorias  || ["N0","N1","N2","N3","N4","N5","N6"];
                  const prioridades = resumenPivot.prioridades || [];
                  const totGlobal   = resumenPivot.totales?.total || 0;

                  if (prioridades.length === 0) {
                    return <div style={{ textAlign: "center", padding: "20px", color: C.slate, fontSize: "12px" }}>Sin datos de prioridad para el mes seleccionado.</div>;
                  }

                  return (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ borderCollapse: "collapse", fontSize: "10px", minWidth: "480px" }}>
                        <thead>
                          <tr>
                            <th style={{ padding: "4px 10px", textAlign: "left", color: C.slate, fontWeight: "600", fontSize: "10px", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>
                              Categoría
                            </th>
                            {prioridades.map((prio, i) => {
                              const col = colorPrio(prio);
                              const totalCol = resumenPivot.totales?.[prio] || 0;
                              const isColAct = pivotFiltroPrio === prio && pivotFiltroCat === null;
                              return (
                                <th key={i} style={{ padding: "4px 6px", textAlign: "center", borderBottom: "1px solid #e2e8f0", minWidth: "80px", cursor: "pointer" }}
                                  onClick={() => totalCol > 0 && handlePivotCol(prio)}
                                  title={`Filtrar por prioridad ${prio}`}>
                                  <span style={{ background: isColAct ? col.actBg : col.bg, color: isColAct ? "white" : col.color, borderRadius: "4px", padding: "2px 8px", fontSize: "10px", fontWeight: "700", display: "block" }}>
                                    {prio}
                                  </span>
                                  <span style={{ fontSize: "9px", color: isColAct ? col.actBg : C.slate, fontWeight: isColAct ? "700" : "400" }}>
                                    {totalCol}
                                  </span>
                                </th>
                              );
                            })}
                            <th style={{ padding: "4px 8px", textAlign: "center", color: C.slate, fontWeight: "600", fontSize: "10px", borderBottom: "1px solid #e2e8f0" }}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cats.map((cat, ci) => {
                            const fila = resumenPivot.matriz?.[cat] || { total: 0 };
                            const catCfg = CATEGORIAS.find(c => c.nivel === cat) || CATEGORIAS[0];
                            const totalFila = fila.total || 0;
                            const isFilaAct = pivotFiltroCat === cat && pivotFiltroPrio === null;
                            return (
                              <tr key={ci} style={{ borderBottom: "1px solid #f8fafc" }}>
                                <td style={{ padding: "3px 10px", whiteSpace: "nowrap" }}>
                                  <span style={{ background: catCfg.color, color: "white", borderRadius: "10px", padding: "2px 10px", fontWeight: "700", fontSize: "10px" }}>
                                    {cat}
                                  </span>
                                </td>
                                {prioridades.map((prio, ri) => {
                                  const val = fila[prio] || 0;
                                  const pct = totalFila > 0 ? Math.round((val / totalFila) * 100) : 0;
                                  const col = colorPrio(prio);
                                  const isAct = pivotFiltroCat === cat && pivotFiltroPrio === prio;
                                  return (
                                    <td key={ri} style={{ padding: "2px 4px", textAlign: "center" }}>
                                      {val > 0
                                        ? <button onClick={() => handlePivotCelda(cat, prio)}
                                            style={{ width: "100%", border: isAct ? `2px solid ${col.actBg}` : `1px solid ${col.bg}`,
                                              borderRadius: "6px", padding: "3px 5px", cursor: "pointer",
                                              background: isAct ? col.actBg : col.bg, transition: "all 0.12s" }}>
                                            <div style={{ fontSize: "12px", fontWeight: "800", color: isAct ? "white" : col.color, lineHeight: 1 }}>{val}</div>
                                            <div style={{ fontSize: "8px", color: isAct ? "rgba(255,255,255,0.8)" : C.slate }}>{pct}%</div>
                                          </button>
                                        : <span style={{ color: "#e2e8f0", fontSize: "10px" }}>—</span>}
                                    </td>
                                  );
                                })}
                                <td onClick={() => totalFila > 0 && handlePivotFila(cat)}
                                  style={{ padding: "3px 8px", textAlign: "center", fontWeight: "700", fontSize: "11px",
                                    color: isFilaAct ? "white" : C.navy,
                                    background: isFilaAct ? C.navy : "transparent",
                                    borderRadius: "6px", cursor: totalFila > 0 ? "pointer" : "default" }}
                                  title={totalFila > 0 ? `Filtrar todo ${cat}` : ""}>
                                  {totalFila}
                                  <div style={{ fontSize: "8px", fontWeight: "400", opacity: 0.7 }}>
                                    {totGlobal > 0 ? Math.round((totalFila / totGlobal) * 100) : 0}%
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                          {/* Fila TOTAL */}
                          <tr style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
                            <td style={{ padding: "4px 10px", fontWeight: "700", fontSize: "10px", color: C.navy }}>TOTAL</td>
                            {prioridades.map((prio, ri) => {
                              const t = resumenPivot.totales?.[prio] || 0;
                              const pct = totGlobal > 0 ? Math.round((t / totGlobal) * 100) : 0;
                              const col = colorPrio(prio);
                              const isAct = pivotFiltroPrio === prio && pivotFiltroCat === null;
                              return (
                                <td key={ri} onClick={() => t > 0 && handlePivotCol(prio)}
                                  style={{ padding: "4px 6px", textAlign: "center", fontWeight: "700",
                                    color: isAct ? "white" : col.color, fontSize: "11px",
                                    background: isAct ? col.actBg : "transparent",
                                    borderRadius: "6px", cursor: t > 0 ? "pointer" : "default" }}
                                  title={`Filtrar por ${prio}`}>
                                  {t} <span style={{ fontSize: "8px", fontWeight: "400", opacity: 0.8 }}>({pct}%)</span>
                                </td>
                              );
                            })}
                            <td onClick={handlePivotTotal}
                              style={{ padding: "4px 8px", textAlign: "center", fontWeight: "800", fontSize: "12px",
                                color: (!pivotFiltroCat && !pivotFiltroPrio) ? C.slate : "white",
                                background: (!pivotFiltroCat && !pivotFiltroPrio) ? "transparent" : C.navy,
                                borderRadius: "6px", cursor: "pointer" }}
                              title="Ver todos / limpiar filtro">
                              {totGlobal}
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      {(pivotFiltroCat || pivotFiltroPrio) && (
                        <div style={{ marginTop: "10px", padding: "6px 12px", background: "#fef2f2", borderRadius: "8px", fontSize: "11px", color: C.red, fontWeight: "600", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                          🔍 Filtrando:
                          {pivotFiltroCat && <strong>Categoría {pivotFiltroCat}</strong>}
                          {pivotFiltroCat && pivotFiltroPrio && <span>—</span>}
                          {pivotFiltroPrio && <strong>Prioridad {pivotFiltroPrio}</strong>}
                          <button onClick={handlePivotTotal}
                            style={{ marginLeft: "auto", background: C.red, color: "white", border: "none", borderRadius: "4px", padding: "2px 10px", fontSize: "10px", cursor: "pointer" }}>
                            ✕ Limpiar
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()
            }
    </>
  );

  const renderCuadroRecurrencia = () => (
    <>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px", flexWrap: "wrap", gap: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "3px", height: "14px", background: C.red, borderRadius: "2px" }} />
                <span style={{ fontSize: "12px", fontWeight: "700", color: C.navy, textTransform: "uppercase", letterSpacing: "0.05em" }}>Recurrencia N2+ por Jefe</span>
                <span style={{ fontSize: "11px", color: C.slate }}>
                  — {filtroMes === null ? fmtMes(mesesDisp[0]) : (Array.isArray(filtroMes) && filtroMes.length ? fmtMes(filtroMes[0]) : "")}
                </span>
              </div>
              {(recFiltroJefe || recFiltroRec) && (
                <button onClick={() => { setRecFiltroJefe(null); setRecFiltroRec(null); setFiltroJefe(null); }}
                  style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "20px", padding: "3px 10px", fontSize: "11px", color: C.red, fontWeight: "600", cursor: "pointer" }}>
                  ✕ Limpiar filtro
                </button>
              )}
            </div>

            {loadingRec
              ? <div style={{ textAlign: "center", padding: "20px", color: C.slate, fontSize: "12px" }}>Cargando...</div>
              : recurrenciaJefe.length === 0
                ? <div style={{ textAlign: "center", padding: "20px", color: C.slate, fontSize: "12px" }}>Sin datos para el mes seleccionado.</div>
                : (() => {
                    const porJefe = {};
                    recurrenciaJefe.forEach(r => {
                      if (!porJefe[r.jefe]) porJefe[r.jefe] = { jefe: r.jefe, total: 0, filas: [] };
                      porJefe[r.jefe].total += parseInt(r.total_nodos || 0);
                      porJefe[r.jefe].filas.push(r);
                    });
                    const tiposRec = [...new Set(recurrenciaJefe.map(r => r.recurrencia))].sort();
                    const totalGlobal = recurrenciaJefe.reduce((s,r) => s + parseInt(r.total_nodos||0), 0);

                    const colorRec = (rec) => {
                      const r = (rec || "").toLowerCase();
                      if (r.includes("crónic") || r.includes("cronic")) return { bg: "#fee2e2", color: "#991b1b", actBg: "#991b1b" };
                      if (r.includes("recurrente")) return { bg: "#fef9c3", color: "#92400e", actBg: "#92400e" };
                      if (r.includes("primera")) return { bg: "#dbeafe", color: "#1d4ed8", actBg: "#1d4ed8" };
                      return { bg: "#f1f5f9", color: "#475569", actBg: "#475569" };
                    };

                    const handleClick = (jefe, rec) => {
                      // Si se hace clic en la misma celda activa → deseleccionar todo
                      const mismoJefe = recFiltroJefe === jefe;
                      const mismoRec  = recFiltroRec  === rec;
                      if (mismoJefe && mismoRec) {
                        setRecFiltroJefe(null); setRecFiltroRec(null);
                        // NO tocamos filtroJefe para no re-disparar el backend
                      } else {
                        setRecFiltroJefe(jefe || null);
                        setRecFiltroRec(rec  || null);
                        // NO tocamos filtroJefe: el filtro de recurrencia es LOCAL sobre los datos ya cargados
                      }
                      setPagina(1); setPaginaH(1);
                    };

                    // Clic en encabezado de columna (tipo de recurrencia) → filtra solo por tipo, todos los jefes
                    const handleClickColumna = (rec) => {
                      if (recFiltroRec === rec && recFiltroJefe === null) {
                        setRecFiltroRec(null);
                      } else {
                        setRecFiltroRec(rec);
                        setRecFiltroJefe(null);
                        // NO tocamos filtroJefe para no re-disparar el backend
                      }
                      setPagina(1); setPaginaH(1);
                    };

                    // Clic en total de fila (jefe) → filtra todos los tipos de ese jefe
                    const handleClickFilaTotal = (jefe) => {
                      if (recFiltroJefe === jefe && recFiltroRec === null) {
                        setRecFiltroJefe(null);
                        // NO tocamos filtroJefe
                      } else {
                        setRecFiltroJefe(jefe);
                        setRecFiltroRec(null);
                        // NO tocamos filtroJefe: filtro local sobre datos ya cargados
                      }
                      setPagina(1); setPaginaH(1);
                    };

                    // Clic en total global (esquina inf. derecha) → limpiar todos los filtros de recurrencia
                    const handleClickTotalGlobal = () => {
                      setRecFiltroJefe(null); setRecFiltroRec(null);
                      // NO tocamos filtroJefe
                      setPagina(1); setPaginaH(1);
                    };

                    const jefesSorted = Object.values(porJefe).sort((a,b) => b.total - a.total);

                    return (
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ borderCollapse: "collapse", fontSize: "10px" }}>
                          <thead>
                            <tr>
                              <th style={{ padding: "3px 8px", textAlign: "left", color: C.slate, fontWeight: "600", fontSize: "9px", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>Jefe</th>
                              {tiposRec.map((rec, i) => {
                                const col = colorRec(rec);
                                const totalTipo = recurrenciaJefe.filter(r => r.recurrencia === rec).reduce((s,r) => s+parseInt(r.total_nodos||0),0);
                                const isColAct = recFiltroRec === rec && recFiltroJefe === null;
                                return (
                                  <th key={i} style={{ padding: "3px 4px", textAlign: "center", borderBottom: "1px solid #e2e8f0", minWidth: "60px", cursor: "pointer" }}
                                    onClick={() => handleClickColumna(rec)}
                                    title={`Filtrar por ${rec}`}>
                                    <span style={{ background: isColAct ? col.actBg : col.bg, color: isColAct ? "white" : col.color, borderRadius: "4px", padding: "1px 5px", fontSize: "9px", fontWeight: "700", display: "block" }}>{rec}</span>
                                    <span style={{ fontSize: "9px", color: isColAct ? col.actBg : C.slate, fontWeight: isColAct ? "700" : "400" }}>{totalTipo}</span>
                                  </th>
                                );
                              })}
                              <th style={{ padding: "3px 6px", textAlign: "center", color: C.slate, fontWeight: "600", fontSize: "9px", borderBottom: "1px solid #e2e8f0" }}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {jefesSorted.map((row, ji) => (
                              <tr key={ji} style={{ borderBottom: "1px solid #f8fafc" }}>
                                <td style={{ padding: "3px 8px", fontWeight: "600", fontSize: "10px", color: C.navy, whiteSpace: "nowrap", maxWidth: "130px", overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {row.jefe || "Sin jefe"}
                                </td>
                                {tiposRec.map((rec, ri) => {
                                  const fila = row.filas.find(f => f.recurrencia === rec);
                                  const val  = fila ? parseInt(fila.total_nodos || 0) : 0;
                                  const pct  = row.total > 0 ? Math.round((val / row.total) * 100) : 0;
                                  const col  = colorRec(rec);
                                  const isAct = recFiltroJefe === row.jefe && recFiltroRec === rec;
                                  return (
                                    <td key={ri} style={{ padding: "2px 3px", textAlign: "center" }}>
                                      {val > 0
                                        ? <button onClick={() => handleClick(row.jefe, rec)}
                                            style={{ width: "100%", border: isAct ? `2px solid ${col.actBg}` : `1px solid ${col.bg}`,
                                              borderRadius: "6px", padding: "3px 5px", cursor: "pointer",
                                              background: isAct ? col.actBg : col.bg, transition: "all 0.12s" }}>
                                            <div style={{ fontSize: "12px", fontWeight: "800", color: isAct ? "white" : col.color, lineHeight: 1 }}>{val}</div>
                                            <div style={{ fontSize: "8px", color: isAct ? "rgba(255,255,255,0.8)" : C.slate }}>{pct}%</div>
                                          </button>
                                        : <span style={{ color: "#e2e8f0", fontSize: "10px" }}>—</span>}
                                    </td>
                                  );
                                })}
                                <td style={{ padding: "3px 6px", textAlign: "center", fontWeight: "700", fontSize: "11px",
                                  color: recFiltroJefe === row.jefe && recFiltroRec === null ? "white" : C.navy,
                                  background: recFiltroJefe === row.jefe && recFiltroRec === null ? C.navy : "transparent",
                                  borderRadius: "6px", cursor: "pointer" }}
                                  onClick={() => handleClickFilaTotal(row.jefe)}
                                  title={`Filtrar todos los tipos de ${row.jefe}`}>
                                  {row.total}
                                  <div style={{ fontSize: "8px", fontWeight: "400", opacity: 0.7 }}>{totalGlobal > 0 ? Math.round((row.total/totalGlobal)*100) : 0}%</div>
                                </td>
                              </tr>
                            ))}
                            <tr style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
                              <td style={{ padding: "3px 8px", fontWeight: "700", fontSize: "10px", color: C.navy }}>TOTAL</td>
                              {tiposRec.map((rec, ri) => {
                                const t = recurrenciaJefe.filter(r => r.recurrencia === rec).reduce((s,r) => s+parseInt(r.total_nodos||0),0);
                                const pct = totalGlobal > 0 ? Math.round((t/totalGlobal)*100) : 0;
                                const col = colorRec(rec);
                                const isAct = recFiltroRec === rec && recFiltroJefe === null;
                                return (
                                  <td key={ri} onClick={() => handleClickColumna(rec)}
                                    style={{ padding: "3px 6px", textAlign: "center", fontWeight: "700",
                                      color: isAct ? "white" : col.color, fontSize: "10px",
                                      background: isAct ? col.actBg : "transparent",
                                      borderRadius: "6px", cursor: "pointer" }}
                                    title={`Filtrar por ${rec}`}>
                                    {t} <span style={{ fontSize: "8px", fontWeight: "400", opacity: 0.8 }}>({pct}%)</span>
                                  </td>
                                );
                              })}
                              <td onClick={handleClickTotalGlobal}
                                style={{ padding: "3px 6px", textAlign: "center", fontWeight: "800", fontSize: "11px",
                                  color: (!recFiltroJefe && !recFiltroRec) ? C.slate : "white",
                                  background: (!recFiltroJefe && !recFiltroRec) ? "transparent" : C.navy,
                                  borderRadius: "6px", cursor: "pointer" }}
                                title="Ver todos / limpiar filtro de recurrencia">
                                {totalGlobal}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        {(recFiltroJefe || recFiltroRec) && (
                          <div style={{ marginTop: "8px", padding: "6px 12px", background: "#fef2f2", borderRadius: "8px", fontSize: "11px", color: C.red, fontWeight: "600", display: "flex", alignItems: "center", gap: "8px" }}>
                            🔍 Filtrando:
                            {recFiltroJefe && <strong>{recFiltroJefe}</strong>}
                            {recFiltroJefe && recFiltroRec && <span>—</span>}
                            {recFiltroRec  && <strong>{recFiltroRec}</strong>}
                            <button onClick={() => { setRecFiltroJefe(null); setRecFiltroRec(null); setFiltroJefe(null); setPagina(1); setPaginaH(1); }}
                              style={{ marginLeft: "auto", background: C.red, color: "white", border: "none", borderRadius: "4px", padding: "1px 8px", fontSize: "10px", cursor: "pointer" }}>✕ Limpiar</button>
                          </div>
                        )}
                      </div>
                    );
                  })()
            }
    </>
  );

  return (
    <div style={{ fontFamily: "Arial", background: C.light, minHeight: "100vh", paddingTop: "60px" }}>

      {/* ══ BOTONES FIJOS — position fixed, siempre visibles ══ */}
      <div style={{ position: "fixed", top: "12px", right: "20px", zIndex: 9999, display: "flex", gap: "8px" }}>
        <button onClick={() => cambiarVista("actual")}
          style={{ padding: "8px 18px", borderRadius: "8px", border: "none", cursor: "pointer", fontSize: "12px", fontWeight: "700", background: vista === "actual" ? C.red : "#1a2b4a", color: "white", boxShadow: "0 2px 8px rgba(0,0,0,0.25)", whiteSpace: "nowrap" }}>
          Corte Actual
        </button>
        <button onClick={() => cambiarVista("historico")}
          style={{ padding: "8px 18px", borderRadius: "8px", border: "none", cursor: "pointer", fontSize: "12px", fontWeight: "700", background: vista === "historico" ? C.red : "#1a2b4a", color: "white", boxShadow: "0 2px 8px rgba(0,0,0,0.25)", whiteSpace: "nowrap" }}>
          Evolución Mensual
        </button>
      </div>

      <div style={{ padding: "16px 20px 20px" }}>

        {/* Título */}
        <div style={{ marginBottom: "14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: "4px", height: "28px", background: C.red, borderRadius: "2px" }} />
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ color: C.navy, fontSize: "18px", fontWeight: "700" }}>Semáforo de Nodos</div>
                {soloLectura && <BannerSoloLectura />}
              </div>
              <div style={{ fontSize: "11px", color: C.slate }}>Seguimiento de indisponibilidad N0 → N6 — Vista: <strong>{vista === "actual" ? "Corte Actual" : "Evolución Mensual"}</strong></div>
            </div>
          </div>
        </div>

        {/* KPIs VISTA ACTUAL */}
        {vista === "actual" && (
          <div style={{ display: "flex", gap: "8px", marginBottom: "14px", flexWrap: "wrap", opacity: (loading || loadingKpis) ? 0.6 : 1, transition: "opacity 0.15s" }}>
            {CATEGORIAS.map(cat => (
              <KpiCard key={cat.nivel} cat={cat} valor={kpisActual[cat.nivel] || 0} activo={filtroCategoria === cat.nivel} onClick={() => toggleCat(cat.nivel)} />
            ))}
            <div style={{ background: C.navy, color: "white", padding: "10px 14px", borderRadius: "10px", minWidth: "78px", textAlign: "center", flexShrink: 0 }}>
              <div style={{ fontSize: "11px", fontWeight: "600" }}>TOTAL</div>
              <div style={{ fontSize: "20px", fontWeight: "800" }}>{(kpisActual.total || 0).toLocaleString()}</div>
              <div style={{ fontSize: "9px", marginTop: "2px" }}>nodos</div>
            </div>
            <div style={{ background: "#7c3aed", color: "white", padding: "10px 14px", borderRadius: "10px", minWidth: "78px", textAlign: "center", flexShrink: 0 }}>
              <div style={{ fontSize: "11px", fontWeight: "600" }}>OFENSORES</div>
              <div style={{ fontSize: "20px", fontWeight: "800" }}>{(kpisActual.ofensores || 0).toLocaleString()}</div>
              <div style={{ fontSize: "9px", marginTop: "2px" }}>N2 → N6</div>
            </div>
          </div>
        )}

        {/* KPIs VISTA HISTÓRICA
            Nota: NO condicionamos a !loadingH para evitar que las tarjetas se
            desmonten (y "parpadeen") cada vez que el usuario hace click en una
            para filtrar. Los valores mostrados son los últimos disponibles. */}
        {vista === "historico" && (
          <div style={{ display: "flex", gap: "8px", marginBottom: "14px", flexWrap: "wrap", opacity: loadingH ? 0.6 : 1, transition: "opacity 0.15s" }}>
            {CATEGORIAS.map(cat => (
              <KpiCard key={cat.nivel} cat={cat} valor={kpisH[cat.nivel] || 0} activo={filtroCategoria === cat.nivel} onClick={() => toggleCat(cat.nivel)} />
            ))}
            <div style={{ background: C.navy, color: "white", padding: "10px 14px", borderRadius: "10px", minWidth: "78px", textAlign: "center", flexShrink: 0 }}>
              <div style={{ fontSize: "11px", fontWeight: "600" }}>TOTAL</div>
              <div style={{ fontSize: "20px", fontWeight: "800" }}>{(kpisH.total || totalH || 0).toLocaleString()}</div>
              <div style={{ fontSize: "9px", marginTop: "2px" }}>nodos</div>
            </div>
            {/* Tarjeta OFENSORES (igual que en Corte Actual): suma N2 a N6 */}
            <div style={{ background: "#7c3aed", color: "white", padding: "10px 14px", borderRadius: "10px", minWidth: "78px", textAlign: "center", flexShrink: 0 }}>
              <div style={{ fontSize: "11px", fontWeight: "600" }}>OFENSORES</div>
              <div style={{ fontSize: "20px", fontWeight: "800" }}>
                {(["N2","N3","N4","N5","N6"].reduce((s, n) => s + (parseInt(kpisH[n]) || 0), 0)).toLocaleString()}
              </div>
              <div style={{ fontSize: "9px", marginTop: "2px" }}>N2 → N6</div>
            </div>
          </div>
        )}

        {/* FILTROS */}
        <div style={cardS}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
            <div style={{ width: "3px", height: "14px", background: C.red, borderRadius: "2px" }} />
            <span style={{ fontSize: "11px", fontWeight: "700", color: C.navy, textTransform: "uppercase" }}>Filtros</span>
            {filtroCategoria && <span style={{ background: C.red, color: "white", borderRadius: "12px", padding: "1px 10px", fontSize: "10px", fontWeight: "700", marginLeft: "6px", cursor: "pointer" }} onClick={() => setFiltroCategoria("")}>Categoría: {filtroCategoria} ✕</span>}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "10px" }}>

            <DropdownSingle id="mes" label={vista === "historico" ? "MES (hasta)" : "MES"}
              opciones={mesesDisp}
              valor={Array.isArray(filtroMes) ? filtroMes[0] : filtroMes}
              onChange={v => setFiltroMes(v ? [v] : (mesesDisp.length ? [mesesDisp[0]] : null))}
              placeholder="Seleccionar mes"
            />
            <DropdownMulti id="jefe"   label="JEFE DE ZONA" opciones={optsJefe}   valor={filtroJefe}   onChange={v => { setFiltroJefe(v);   setPagina(1); }} placeholder="Todos" />
            <DropdownMulti id="aliado" label="ALIADO"       opciones={optsAliado} valor={filtroAliado} onChange={v => { setFiltroAliado(v); setPagina(1); }} placeholder="Todos" />
            <DropdownMulti id="ciudad" label="CIUDAD"       opciones={optsCiudad} valor={filtroCiudad} onChange={v => { setFiltroCiudad(v); setPagina(1); }} placeholder="Todas" />
            <DropdownMultiSearch id="nodo" label="NODO" opciones={optsNodos} valor={filtroNodo} onChange={v => { setFiltroNodo(v); setPagina(1); }} placeholder="Todos" />
            <DropdownMulti id="riesgo" label="RIESGO"       opciones={["OK","Bajo","Medio","Alto","Crítico"]} valor={filtroRiesgo} onChange={v => { setFiltroRiesgo(v); setPagina(1); }} placeholder="Todos" />
            <DropdownMulti id="prioridad" label="PRIORIDAD" opciones={optsPrioridad} valor={filtroPrioridad} onChange={v => { setFiltroPrioridad(v); setPagina(1); setPaginaH(1); }} placeholder="Todas" />

            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button onClick={limpiar} style={{ width: "100%", padding: "8px 10px", background: "#f1f5f9", color: C.slate, cursor: "pointer", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "12px", fontWeight: "600" }}>
                Limpiar filtros
              </button>
            </div>
          </div>

          {/* Tabla de referencia de niveles de riesgo */}
          <div style={{ marginTop: "14px", paddingTop: "12px", borderTop: "1px solid #f1f5f9" }}>
            <div style={{ fontSize: "10px", fontWeight: "700", color: C.slate, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>
              Referencia — Niveles de Riesgo
            </div>
            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
              {[
                { label: "OK",      emoji: "✅", color: "#16a34a", bg: "#dcfce7", desc: "N0 — Disponibilidad 100%" },
                { label: "Bajo",    emoji: "🟢", color: "#16a34a", bg: "#f0fdf4", desc: "< 25% del rango consumido" },
                { label: "Medio",   emoji: "🟡", color: "#ca8a04", bg: "#fefce8", desc: "25% – 50% del rango consumido" },
                { label: "Alto",    emoji: "🟠", color: "#ea580c", bg: "#fff7ed", desc: "50% – 85% del rango consumido" },
                { label: "Crítico", emoji: "🔴", color: "#991b1b", bg: "#fee2e2", desc: "> 85% del rango o N6" },
              ].map((r, i) => (
                <div key={i} onClick={() => setFiltroRiesgo(prev => {
                    const arr = prev === null ? [] : prev;
                    return arr.includes(r.label) ? arr.filter(x => x !== r.label) : [...arr, r.label];
                  })}
                  style={{ display: "flex", alignItems: "center", gap: "8px",
                    background: (Array.isArray(filtroRiesgo) && filtroRiesgo.includes(r.label)) ? r.bg : "#f8fafc",
                    border: `1px solid ${(Array.isArray(filtroRiesgo) && filtroRiesgo.includes(r.label)) ? r.color : "#e2e8f0"}`,
                    borderRadius: "8px", padding: "5px 12px", cursor: "pointer", transition: "all 0.15s" }}>
                  <span style={{ fontSize: "13px" }}>{r.emoji}</span>
                  <div>
                    <div style={{ fontSize: "11px", fontWeight: "700", color: r.color }}>{r.label}</div>
                    <div style={{ fontSize: "10px", color: C.slate }}>{r.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ══ CUADRO RESUMEN CATEGORIA x PRIORIDAD (tabla pivote dinámica) ══
            Se muestra en ambas vistas. En "Corte Actual" es el único resumen (card solo).
            En "Evolución Mensual" se muestra LADO A LADO con "Recurrencia N2+ por Jefe". */}
        {resumenPivot && vista === "actual" && (
          <div style={cardS}>
            {renderCuadroPrioridad()}
          </div>
        )}

        {vista === "actual" && (
          <div style={cardS}>
            {loading ? <div style={{ textAlign: "center", padding: "60px", color: C.slate }}>Cargando datos...</div> : <>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
                  <thead><tr>
                    {[
                      { label: "Nodo",               col: "id_nodo",     align: "left" },
                      { label: "Nombre",              col: "nombre_nodo", align: "left" },
                      { label: "Ciudad",              col: "ciudad",      align: "left" },
                      { label: "Jefe Zona",           col: "nombre_jefe", align: "left" },
                      { label: "Aliado",              col: "aliado",      align: "left" },
                      { label: "Mes",                 col: "",            align: "center" },
                      { label: "Fecha Corte",         col: "",            align: "center" },
                      { label: "Categoría",           col: "categoria",   align: "center" },
                      { label: "% Disp.",             col: "porc_dispo",  align: "center" },
                      { label: "H. Indisp.",          col: "h_indisp",    align: "center" },
                      { label: "Faltan para sig. cat.", col: "",          align: "center" },
                      { label: "Riesgo",              col: "riesgo",      align: "center" },
                    ].map(({ label, col, align }, i) => (
                      <th key={i}
                        onClick={col ? () => { setSortCol(c => c === col ? col : col); setSortDir(d => sortCol === col ? (d === "asc" ? "desc" : "asc") : "asc"); } : undefined}
                        style={{ ...thS, textAlign: align, cursor: col ? "pointer" : "default", userSelect: "none",
                          background: sortCol === col ? "#2d3f55" : "#1a2b4a",
                          borderBottom: sortCol === col ? `2px solid ${C.red}` : "none" }}>
                        {label}{col ? (sortCol === col ? (sortDir === "asc" ? " ▲" : " ▼") : " ⇅") : ""}
                      </th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {datosFilt.length === 0
                      ? <tr><td colSpan={12} style={{ textAlign: "center", padding: "40px", color: C.slate }}>No hay registros.</td></tr>
                      : datosFilt.map((n, i) => {
                          const cat = n.catReal; const sem = n.semaforo;
                          return (
                            <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                              <td style={{ ...tdS, fontWeight: "700", color: C.navy }}>{n.id_nodo}</td>
                              <td style={tdS}>{n.nombre_nodo || "—"}</td>
                              <td style={tdS}>{n.ciudad || "—"}</td>
                              <td style={tdS}>{n.nombre_jefe || "—"}</td>
                              <td style={tdS}>{n.aliado || "—"}</td>
                              <td style={{ ...tdS, textAlign: "center" }}>{fmtMes(n.mes_anio)}</td>
                              <td style={{ ...tdS, textAlign: "center", fontSize: "10px", color: C.slate }}>{n.fecha_corte || "—"}</td>
                              <td style={{ ...tdS, textAlign: "center" }}>
                                <span style={{ background: cat.color, color: "white", borderRadius: "20px", padding: "2px 12px", fontWeight: "700", fontSize: "11px" }}>{cat.nivel}</span>
                              </td>
                              <td style={{ ...tdS, textAlign: "center" }}>{parseFloat(n.porc_dispo_real ?? 100).toFixed(2)}%</td>
                              <td style={{ ...tdS, textAlign: "center" }}>{fmtH(n.horasCalc)}</td>
                              <td style={{ ...tdS, textAlign: "center" }}>
                                {cat.nivel === "N6" ? <span style={{ color: "#991b1b", fontWeight: "700" }}>Máx. categoría</span>
                                  : <div><div style={{ fontSize: "11px", fontWeight: "600", color: sem.color }}>{sem.texto}</div><div style={{ fontSize: "10px", color: C.slate }}>para {sem.siguiente}</div></div>}
                              </td>
                              <td style={{ ...tdS, textAlign: "center" }}>
                                {cat.nivel === "N6" ? <span style={{ background: "#991b1b", color: "white", borderRadius: "6px", padding: "2px 10px", fontSize: "10px", fontWeight: "700" }}>CRÍTICO</span>
                                  : cat.nivel === "N0" ? <span style={{ background: "#16a34a", color: "white", borderRadius: "6px", padding: "2px 10px", fontSize: "10px", fontWeight: "700" }}>OK</span>
                                  : <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                                      <div style={{ width: "80px", height: "8px", background: "#e2e8f0", borderRadius: "4px", overflow: "hidden" }}>
                                        <div style={{ width: `${sem.pct}%`, height: "100%", background: sem.color, borderRadius: "4px" }} />
                                      </div>
                                      <span style={{ fontSize: "10px", color: sem.color, fontWeight: "700" }}>{sem.pct}%</span>
                                    </div>}
                              </td>
                            </tr>
                          );
                        })}
                  </tbody>
                </table>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "14px", flexWrap: "wrap", gap: "8px" }}>
                <span style={{ fontSize: "12px", color: C.slate }}>Página <strong>{pagina}</strong> de <strong>{totPag}</strong> — Total: <strong>{total.toLocaleString()}</strong> nodos</span>
                <div style={{ display: "flex", gap: "6px" }}>
                  <button onClick={() => cargar(1)} disabled={pagina === 1} style={btnPag(pagina === 1)}>« Primera</button>
                  <button onClick={() => cargar(pagina - 1)} disabled={pagina === 1} style={btnPag(pagina === 1)}>‹ Ant.</button>
                  <button onClick={() => cargar(pagina + 1)} disabled={pagina >= totPag} style={btnPag(pagina >= totPag)}>Sig. ›</button>
                  <button onClick={() => cargar(totPag)} disabled={pagina >= totPag} style={btnPag(pagina >= totPag)}>Última »</button>
                </div>
              </div>
            </>}
          </div>
        )}

        {/* ══ RESUMEN HISTÓRICO: dos cuadros lado a lado ══
            Izquierda: Categoría × Prioridad (tabla pivote).
            Derecha:  Recurrencia N2+ por Jefe — queda pegado al anterior
            para facilitar comparar visualmente ambos cuadros. */}
        {vista === "historico" && (
          <div style={{ display: "flex", gap: "12px", marginBottom: "16px", flexWrap: "wrap", alignItems: "flex-start" }}>
            {resumenPivot && (
              <div style={{ ...cardS, flex: "0 0 auto", minWidth: "320px", marginBottom: 0 }}>
                {renderCuadroPrioridad()}
              </div>
            )}
            <div style={{ ...cardS, flex: "0 0 auto", minWidth: "320px", marginBottom: 0 }}>
              {renderCuadroRecurrencia()}
            </div>
          </div>
        )}


        {/* ══ TABLA EVOLUCIÓN MENSUAL ══ */}
        {vista === "historico" && (
          <div style={cardS}>
            {loadingH ? <div style={{ textAlign: "center", padding: "60px", color: C.slate }}>Cargando evolución mensual...</div> : <>
              <div style={{ fontSize: "11px", color: C.slate, marginBottom: "12px" }}>
                Hasta <strong>{fmtMes(filtroMes)}</strong> — Meses: <strong>{mesesHist.length}</strong>{filtroCategoria && <span style={{ marginLeft: "8px", color: C.red, fontWeight: "700" }}>· Filtro categoría: {filtroCategoria}</span>}
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
                  <thead><tr>
                    <th style={{ ...thS, minWidth: "80px" }}>Nodo</th>
                    <th style={{ ...thS, minWidth: "130px" }}>Nombre</th>
                    <th style={thS}>Ciudad</th><th style={thS}>Jefe</th>
                    {mesesHist.map((m, i) => <th key={i} style={{ ...thS, textAlign: "center", minWidth: "85px" }}>{fmtMes(m)}</th>)}
                  </tr></thead>
                  <tbody>
                    {nodosHist.length === 0
                      ? <tr><td colSpan={4 + mesesHist.length} style={{ textAlign: "center", padding: "40px", color: C.slate }}>No hay registros.</td></tr>
                      : nodosHist.map((nodo, i) => (
                          <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                            <td style={{ ...tdS, fontWeight: "700", color: C.navy }}>{nodo.id_nodo}</td>
                            <td style={tdS}>{nodo.nombre_nodo || "—"}</td>
                            <td style={tdS}>{nodo.ciudad || "—"}</td>
                            <td style={tdS}>{nodo.nombre_jefe || "—"}</td>
                            {mesesHist.map((m, mi) => {
                              const reg = nodo.meses[m];
                              if (!reg) return <td key={mi} style={{ ...tdS, textAlign: "center", background: "#f1f5f9", color: "#cbd5e1" }}>—</td>;
                              const cat = reg.catReal; const sem = reg.semaforo;
                              return (
                                <td key={mi} style={{ ...tdS, textAlign: "center", background: cat.colorLight }}
                                  title={`${reg.id_nodo} | ${fmtMes(m)} | ${parseFloat(reg.porc_dispo_real ?? 100).toFixed(2)}% disp. | ${fmtH(reg.horasCalc)} indisp. | Corte: ${reg.fecha_corte}`}>
                                  <span style={{ background: cat.color, color: "white", borderRadius: "12px", padding: "1px 8px", fontWeight: "700", fontSize: "10px" }}>{cat.nivel}</span>
                                  {cat.nivel !== "N6" && cat.nivel !== "N0" && <div style={{ fontSize: "9px", color: sem.color, fontWeight: "600", marginTop: "2px" }}>{sem.pct}%</div>}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "14px", flexWrap: "wrap", gap: "8px" }}>
                <span style={{ fontSize: "12px", color: C.slate }}>Página <strong>{paginaH}</strong> de <strong>{totPagH}</strong> — Total nodos: <strong>{(kpisH.total || totalH || 0).toLocaleString()}</strong></span>
                <div style={{ display: "flex", gap: "6px" }}>
                  <button onClick={() => cargarH(1)} disabled={paginaH === 1} style={btnPag(paginaH === 1)}>« Primera</button>
                  <button onClick={() => cargarH(paginaH - 1)} disabled={paginaH === 1} style={btnPag(paginaH === 1)}>‹ Ant.</button>
                  <button onClick={() => cargarH(paginaH + 1)} disabled={paginaH >= totPagH} style={btnPag(paginaH >= totPagH)}>Sig. ›</button>
                  <button onClick={() => cargarH(totPagH)} disabled={paginaH >= totPagH} style={btnPag(paginaH >= totPagH)}>Última »</button>
                </div>
              </div>
            </>}
          </div>
        )}

        {/* Leyenda */}
        <div style={{ ...cardS, marginBottom: 0 }}>
          <div style={{ fontSize: "11px", fontWeight: "700", color: C.navy, marginBottom: "8px" }}>LEYENDA DE CATEGORÍAS</div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {CATEGORIAS.map(cat => (
              <div key={cat.nivel} style={{ display: "flex", alignItems: "center", gap: "6px", background: cat.colorLight, border: `1px solid ${cat.color}`, borderRadius: "8px", padding: "5px 10px" }}>
                <span style={{ background: cat.color, color: "white", borderRadius: "10px", padding: "1px 8px", fontWeight: "700", fontSize: "11px" }}>{cat.nivel}</span>
                <span style={{ fontSize: "10px", color: cat.color, fontWeight: "600" }}>
                  {cat.nivel === "N0" ? "Disp. = 100%" :
                 cat.nivel === "N1" ? "≥ 99.2% — ≤ 5h 57min" :
                 cat.nivel === "N2" ? "≥ 98.3% — ≤ 12h 39min" :
                 cat.nivel === "N3" ? "≥ 96.7% — ≤ 24h 33min" :
                 cat.nivel === "N4" ? "≥ 94.7% — ≤ 39h 26min" :
                 cat.nivel === "N5" ? "≥ 85% — ≤ 111h 36min" : "< 85%"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}